import { forwardRef, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode, type Ref } from 'react';
import { PHONE_COUNTRIES, findCountry, type PhoneCountry } from '@repletoryreach/sdk-utils/phone';
import { useMergedRef, useOnClickOutside } from '@repletoryreach/sdk-utils/hooks';
import { Portal } from '../../core/Portal/Portal.js';
import { computePosition } from '../../overlays/floating-positioner.js';
import { InputWrapper, type InputWrapperBaseProps } from '../shared/InputBase.js';
import { usePhoneDefaultCountry } from './PhoneDefaults.js';
import './PhoneInput.css';

export interface CountryPickerLabels {
  /** Placeholder of the search box. */
  search?: string;
  /** Shown when nothing matches the search. */
  noMatches?: string;
  /** Accessible name of the trigger when no visible label is given. */
  country?: string;
}

export interface CountryPickerProps extends InputWrapperBaseProps {
  /** ISO 3166-1 alpha-2 of the selected country. */
  value?: string | null;
  onChange?: (iso: string, country: PhoneCountry) => void;
  /** Used when `value` is empty: the provider's country, the browser's, then ZA. */
  defaultCountry?: string | null;
  /** `field` shows flag, name and code in a full-width control; `compact` shows flag and code only (what `PhoneInput` uses). */
  variant?: 'field' | 'compact';
  /** Restrict the list, for example to the countries the platform bills in. */
  countries?: readonly PhoneCountry[];
  labels?: CountryPickerLabels;
  disabled?: boolean;
  id?: string;
  name?: string;
  /** Where focus goes when Tab leaves the open list; `PhoneInput` passes its number field. */
  onTabOut?: () => void;
  'aria-label'?: string;
}

function matches(c: PhoneCountry, q: string): boolean {
  if (!q) return true;
  const digits = q.replace(/^\+/, '');
  return c.name.toLowerCase().includes(q)
    || c.iso.toLowerCase() === q
    || (digits.length > 0 && /^[0-9]+$/.test(digits) && c.dial.startsWith(digits));
}

/** The countries that match, the ones whose name starts with the query first. */
function filterCountries(list: readonly PhoneCountry[], query: string): PhoneCountry[] {
  const q = query.trim().toLowerCase();
  const hits = list.filter((c) => matches(c, q));
  if (!q) return hits;
  const starts = hits.filter((c) => c.name.toLowerCase().startsWith(q) || c.iso.toLowerCase() === q);
  const rest = hits.filter((c) => !starts.includes(c));
  return [...starts, ...rest];
}

/**
 * A searchable country picker: flag, name and dialling code, with type-ahead
 * on the name, the ISO code or the code (`+27` or `27`).
 *
 * Keyboard: Enter, Space or Arrow Down on the trigger opens the list with the
 * search box focused; typing a letter on the closed trigger opens it with
 * that letter as the search. In the list, the arrows move, Enter chooses,
 * Escape closes and returns to the trigger, Tab closes and moves on.
 */
export const CountryPicker = forwardRef(function CountryPicker(
  props: CountryPickerProps,
  ref: Ref<HTMLButtonElement>
): ReactNode {
  const {
    value, onChange, defaultCountry, variant = 'field', countries = PHONE_COUNTRIES, labels,
    disabled, id, name, onTabOut,
    label, description, error, required, withAsterisk, size, className, style,
    'aria-label': ariaLabel
  } = props;

  const fallback = usePhoneDefaultCountry(defaultCountry);
  const selected = findCountry(value) ?? findCountry(fallback) ?? countries[0];

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [coords, setCoords] = useState<{ x: number; y: number } | null>(null);
  const [menuEl, setMenuEl] = useState<HTMLDivElement | null>(null);

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const mergedTrigger = useMergedRef(ref, triggerRef);
  const listId = useId();

  const visible = useMemo(() => filterCountries(countries, query), [countries, query]);

  const openList = (initialQuery = '') => {
    if (disabled) return;
    setQuery(initialQuery);
    const idx = initialQuery ? 0 : Math.max(0, countries.findIndex((c) => c.iso === selected?.iso));
    setActive(idx);
    setOpen(true);
  };
  const closeList = (returnFocus = true) => {
    setOpen(false);
    setQuery('');
    if (returnFocus) triggerRef.current?.focus();
  };
  const choose = (c: PhoneCountry) => {
    onChange?.(c.iso, c);
    closeList();
  };

  // Position below the trigger, flipping when the viewport runs out.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !menuEl) return;
    const measure = () => {
      const anchor = triggerRef.current;
      if (!anchor) return;
      const a = anchor.getBoundingClientRect();
      const m = menuEl.getBoundingClientRect();
      const pos = computePosition(a, { width: m.width, height: m.height }, { placement: 'bottom-start', offset: 4 });
      setCoords({ x: pos.x, y: pos.y });
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open, menuEl]);

  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (active >= visible.length) setActive(Math.max(0, visible.length - 1));
  }, [visible.length, active]);

  useEffect(() => {
    if (!open || !menuRef.current) return;
    const el = menuRef.current.querySelector<HTMLElement>(`[data-index="${active}"]`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [active, open]);

  useOnClickOutside(menuRef, (e) => {
    if (open && !triggerRef.current?.contains(e.target as Node)) closeList(false);
  });

  const onTriggerKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openList(); return; }
    if (e.key.length === 1 && /[a-z0-9+]/i.test(e.key)) { e.preventDefault(); openList(e.key); }
  };

  const onSearchKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(visible.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
    else if (e.key === 'Home') { e.preventDefault(); setActive(0); }
    else if (e.key === 'End') { e.preventDefault(); setActive(Math.max(0, visible.length - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); const c = visible[active]; if (c) choose(c); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); closeList(); }
    else if (e.key === 'Tab') {
      e.preventDefault();
      closeList(!onTabOut);
      onTabOut?.();
    }
  };

  const activeId = visible[active] ? `${listId}-${visible[active].iso}` : undefined;
  const menu = open ? (
    <Portal>
      <div
        ref={(node) => { menuRef.current = node; setMenuEl(node); }}
        className="rr-country-menu"
        style={{ left: coords?.x ?? -9999, top: coords?.y ?? -9999 }}
        data-rr-country-menu
      >
        <input
          ref={searchRef}
          type="text"
          role="combobox"
          className="rr-country-menu__search"
          placeholder={labels?.search ?? 'Search by country or code'}
          aria-label={labels?.search ?? 'Search by country or code'}
          aria-autocomplete="list"
          aria-expanded
          aria-controls={listId}
          aria-activedescendant={activeId}
          autoComplete="off"
          value={query}
          onChange={(e) => { setQuery(e.currentTarget.value); setActive(0); }}
          onKeyDown={onSearchKey}
        />
        <ul id={listId} role="listbox" className="rr-country-menu__list" aria-label={labels?.country ?? 'Country'}>
          {visible.length === 0 ? (
            <li className="rr-country-menu__empty" role="presentation">{labels?.noMatches ?? 'No country matches'}</li>
          ) : visible.map((c, i) => (
            <li
              key={c.iso}
              id={`${listId}-${c.iso}`}
              role="option"
              aria-selected={c.iso === selected?.iso}
              data-index={i}
              data-active={i === active || undefined}
              className="rr-country-menu__option"
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(c)}
            >
              <span className="rr-country-menu__flag" aria-hidden>{c.flag}</span>
              <span className="rr-country-menu__name">{c.name}</span>
              <span className="rr-country-menu__dial">+{c.dial}</span>
            </li>
          ))}
        </ul>
      </div>
    </Portal>
  ) : null;

  const triggerLabel = ariaLabel ?? labels?.country ?? 'Country';
  const trigger = (inputId: string, describedBy: string | undefined, invalid: boolean, extraClass?: string) => (
    <button
      ref={mergedTrigger}
      id={inputId}
      type="button"
      className={['rr-country-trigger', `rr-country-trigger--${variant}`, extraClass].filter(Boolean).join(' ')}
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-controls={open ? listId : undefined}
      aria-label={variant === 'compact' ? `${triggerLabel}: ${selected?.name ?? ''} +${selected?.dial ?? ''}` : undefined}
      aria-describedby={describedBy}
      aria-invalid={invalid || undefined}
      disabled={disabled}
      onClick={() => (open ? closeList() : openList())}
      onKeyDown={onTriggerKey}
      data-country={selected?.iso}
    >
      <span className="rr-country-trigger__flag" aria-hidden>{selected?.flag}</span>
      {variant === 'field' ? <span className="rr-country-trigger__name">{selected?.name}</span> : null}
      <span className="rr-country-trigger__dial">+{selected?.dial}</span>
      <svg className="rr-country-trigger__chevron" width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <polyline points="1,1 5,5 9,1" />
      </svg>
    </button>
  );

  if (variant === 'compact') {
    return (
      <>
        {trigger(id ?? `${listId}-trigger`, undefined, false)}
        {name ? <input type="hidden" name={name} value={selected?.iso ?? ''} /> : null}
        {menu}
      </>
    );
  }

  return (
    <>
      <InputWrapper
        label={label}
        description={description}
        error={error}
        required={required}
        withAsterisk={withAsterisk}
        size={size}
        className={className}
        style={style}
        inputId={id}
      >
        {({ inputId, describedBy, invalid, className: ctlClass }) => trigger(inputId, describedBy, invalid, ctlClass)}
      </InputWrapper>
      {name ? <input type="hidden" name={name} value={selected?.iso ?? ''} /> : null}
      {menu}
    </>
  );
});
